import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { environment } from '../../../environments/environment';
import { BackLinkComponent } from '../../shared/components/back-link/back-link.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { LoadingSpinnerComponent } from '../../shared/components/loading-spinner/loading-spinner.component';

interface FishType {
  id: number;
  fish_name: string;
  fish_full_name: string | null;
  other_names: string | null;
  fish_display_color: string;
  active: boolean;
  icon: string | null;
}

interface EditForm {
  my_name: string;
  full_name: string;
  other_names: string;
  display_color: string;
  active: boolean;
  icon: string;
}

const EMPTY_FORM: EditForm = {
  my_name: '',
  full_name: '',
  other_names: '',
  display_color: '#ff0000',
  active: true,
  icon: '',
};

@Component({
  selector: 'app-fish-types',
  templateUrl: './fish-types.component.html',
  styleUrls: ['./fish-types.component.scss'],
  standalone: true,
  imports: [
    CommonModule, RouterModule, FormsModule,
    MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatTableModule,
    BackLinkComponent, PageHeaderComponent, LoadingSpinnerComponent
  ],
})
export class FishTypesSettingsComponent implements OnInit {
  fishTypes: FishType[] = [];
  dataSource = new MatTableDataSource<FishType>();
  displayedColumns = ['color', 'name', 'full_name', 'other_names', 'status', 'actions'];
  
  isLoading = true;
  errorMessage: string | null = null;

  editingId: number | null = null; // null = not editing; -1 = adding new
  editForm: EditForm = { ...EMPTY_FORM };
  isSaving = false;

  constructor(private http: HttpClient, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.loadFishTypes();
  }

  private loadFishTypes(): void {
    this.isLoading = true;
    this.http.get<FishType[]>(`${environment.apiUrl}/fish_types?include_inactive=true`).subscribe({
      next: (data) => {
        this.fishTypes = data;
        this.dataSource.data = this.fishTypes;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.errorMessage = 'Failed to load fish types.';
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  startAdd(): void {
    this.editingId = -1;
    this.editForm = { ...EMPTY_FORM };
    this.dataSource.data = [
      { id: -1, fish_name: '', fish_full_name: '', fish_display_color: '#ff0000', other_names: '', active: true, icon: '' },
      ...this.fishTypes
    ];
    this.cdr.detectChanges();
  }

  startEdit(ft: FishType): void {
    this.editingId = ft.id;
    this.editForm = {
      my_name: ft.fish_name,
      full_name: ft.fish_full_name ?? '',
      other_names: ft.other_names ?? '',
      display_color: ft.fish_display_color,
      active: ft.active,
      icon: ft.icon ?? '',
    };
    this.dataSource.data = this.fishTypes; // ensure we aren't still showing the 'add' temp row
    this.cdr.detectChanges();
  }

  cancelEdit(): void {
    this.editingId = null;
    this.dataSource.data = this.fishTypes;
    this.cdr.detectChanges();
  }

  saveNew(): void {
    if (!this.editForm.my_name.trim() || !this.editForm.display_color) return;
    this.isSaving = true;
    this.http.post<FishType>(`${environment.apiUrl}/fish_types`, {
      my_name: this.editForm.my_name.trim(),
      full_name: this.editForm.full_name.trim() || null,
      other_names: this.editForm.other_names.trim() || null,
      display_color: this.editForm.display_color,
      active: this.editForm.active,
      icon: this.editForm.icon.trim() || null,
    }).subscribe({
      next: (created) => {
        this.fishTypes = [...this.fishTypes, created];
        this.dataSource.data = this.fishTypes;
        this.editingId = null;
        this.isSaving = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isSaving = false;
        this.cdr.detectChanges();
      },
    });
  }

  saveEdit(id: number): void {
    this.isSaving = true;
    this.http.patch<FishType>(`${environment.apiUrl}/fish_types/${id}`, {
      my_name: this.editForm.my_name.trim(),
      full_name: this.editForm.full_name.trim() || null,
      other_names: this.editForm.other_names.trim() || null,
      display_color: this.editForm.display_color,
      active: this.editForm.active,
      icon: this.editForm.icon.trim() || null,
    }).subscribe({
      next: (updated) => {
        this.fishTypes = this.fishTypes.map(ft => ft.id === id ? updated : ft);
        this.dataSource.data = this.fishTypes;
        this.editingId = null;
        this.isSaving = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isSaving = false;
        this.cdr.detectChanges();
      },
    });
  }

  toggleActive(ft: FishType): void {
    const action = ft.active ? 'disable' : 'enable';
    if (ft.active && !confirm(`Disable "${ft.fish_name}"? It will no longer appear in the fishing interface.`)) return;
    this.http.patch<FishType>(`${environment.apiUrl}/fish_types/${ft.id}`, { active: !ft.active }).subscribe({
      next: (updated) => {
        this.fishTypes = this.fishTypes.map(f => f.id === ft.id ? updated : f);
        this.dataSource.data = this.fishTypes;
        this.cdr.detectChanges();
      },
      error: () => {},
    });
  }
}
